import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { makeUniqueId } from '@apollo/client/utilities'
import {
    ArrowDropDown,
    ArrowDropUp,
    Edit,
    Favorite,
    NoteAlt,
} from '@mui/icons-material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Link as MuiLink,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material'
import {
    DataGridPro,
    GridFooterContainer,
    GridPagination,
    GridPreferencePanelsValue,
    useGridApiContext,
    useGridApiRef,
    type DataGridProProps,
    type GridColDef,
    type GridColumnVisibilityModel,
    type GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import {
    GetFilteredRostersWithCandidateQuery,
    RosterStatus,
    SortField,
    SortIndexField,
} from '__generated__/graphql'
import { formatDate } from 'date-fns'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import RosterStatusPill from 'recruiter/components/RosterStatusPill'
import { SourceStatusPill } from 'recruiter/components/SourceStatusPill'
import { GetFilteredRostersWithCandidate } from 'recruiter/contexts'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { GetRosterCandidateRecruiterNotes } from 'recruiter/graphql/rosterProfile.gql'
import { UpdateProcessStatusAcrossRosters } from 'recruiter/graphql/updateProcessStatus.gql'
import { useRosterSearch } from 'recruiter/hooks'
import { CandidateRosterNoteDrawer } from 'recruiter/pages/Rosters/RosterProfile/CandidateRosterNoteDrawer'
import ProcessStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditCell'
import SourceStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/SourceStatusEditCell'
import { recruiterRouteConfig } from 'recruiter/routes'
import { CenteredBox } from 'shared/components/CenteredBox'
import useToast from 'shared/components/Toast/useToast'
import { useLocalStorage } from 'shared/hooks/useLocalStorage'
import {
    getPxAsRem,
    makeSortModel,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'
import RosterPanelContent from '../RosterPanelContent'

const TableFooter = () => {
    const apiRef = useGridApiContext()
    const allColumns = apiRef.current.getAllColumns()
    const visibleColumns = apiRef.current.getVisibleColumns()
    const showCount = visibleColumns.length < allColumns.length
    return (
        <GridFooterContainer sx={{ pl: 1 }}>
            <Button
                variant="text"
                size="small"
                onClick={() =>
                    apiRef.current.showPreferences(
                        GridPreferencePanelsValue.columns
                    )
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
                Columns
                {showCount &&
                    ` (${visibleColumns.length}/${allColumns.length})`}
            </Button>
            <Box flex={1} />
            <GridPagination />
        </GridFooterContainer>
    )
}

export type CandidateRostersTableRowModel =
    GetFilteredRostersWithCandidateQuery['rosterSearch']['rosters'][number]

type CandidateRostersTableColumnDefinition =
    GridColDef<CandidateRostersTableRowModel> & {
        sortIndexField?: SortIndexField
    }

export function CandidateRostersTable() {
    const {
        data,
        isLoading,
        totalItems,
        tableState,
        setTableState,
        refetch,
        selectedRosterIds,
        setSelectedRosterIds,
        selectAllEditableRosters,
        isSelectingAll,
        editableTotalCount,
    } = useRosterSearch()

    const gridApi = useGridApiRef()
    const theme = useTheme()
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useLocalStorage<GridColumnVisibilityModel>(
            'candidate-rosters-on-columns',
            {}
        )
    const [noteDrawerRosterCandidateId, setNoteDrawerRosterCandidateId] =
        useState<string | null>(null)
    const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
        null
    )
    const [processStatusOpen, setProcessStatusOpen] = useState(false)
    const actionsButtonRef = useRef<HTMLButtonElement>(null)
    const apolloClient = useApolloClient()
    const toast = useToast()

    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)
    const [updateProcessStatus] = useMutation(
        UpdateProcessStatusAcrossRosters,
        {
            refetchQueries: [GetFilteredRostersWithCandidate],
        }
    )

    const handleNoteSaved = () => {
        refetch()
        apolloClient.refetchQueries({
            include: [GetRosterCandidateRecruiterNotes],
        })
    }

    const handleSortModelChange: DataGridProProps['onSortModelChange'] = (
        model
    ) => {
        if (model.length === 0) {
            setTableState({ sort: [], page: 1 })
            return
        }

        const sortableColumns = columns.filter(
            (column) => (column.sortable ?? true) && column.sortIndexField
        )

        if (
            !model.some(({ field }) =>
                sortableColumns.some((column) => column.field === field)
            )
        ) {
            return
        }

        const updatedSortFields = model.reduce<SortField[]>(
            (sortFields, model) => {
                const column = sortableColumns.find(
                    (column) => model.field === column.field
                )

                if (column?.sortIndexField && model.sort) {
                    sortFields.push({
                        field: column.sortIndexField,
                        order: model.sort,
                    })
                }
                return sortFields
            },
            []
        )

        setTableState({ sort: updatedSortFields, page: 1 })
    }

    const handlePaginationModelChange = (model: {
        page: number
        pageSize: number
    }) => {
        setTableState({ page: model.page + 1, limit: model.pageSize })
    }

    const getDetailPanelContent: DataGridProProps<CandidateRostersTableRowModel>['getDetailPanelContent'] =
        (params) => <RosterPanelContent data={params} />

    const getDetailPanelHeight: DataGridProProps<CandidateRostersTableRowModel>['getDetailPanelHeight'] =
        () => 'auto'

    const columns: CandidateRostersTableColumnDefinition[] = [
        {
            field: 'rosterName',
            headerName: 'Search',
            flex: 1,
            sortIndexField: SortIndexField.RosterName,
            valueGetter: (value, row) =>
                row.rosterCandidate?.roster?.rosterName,
            renderCell: ({ row }) => (
                <MuiLink
                    onClick={() => setTableState({ filters: [] })}
                    component={Link}
                    to={`${recruiterRouteConfig.rosters.path}/${row.rosterCandidate?.roster?.id}`}
                >
                    {row.rosterCandidate?.roster?.rosterName}
                </MuiLink>
            ),
        },
        {
            field: 'company',
            headerName: 'Company',
            flex: 1,
            sortIndexField: SortIndexField.RosterCompany,
            valueGetter: (value, row) =>
                row.rosterCandidate?.roster?.company?.value,
            renderCell: ({ row }) => (
                <MuiLink
                    onClick={() => setTableState({ filters: [] })}
                    component={Link}
                    to={`${recruiterRouteConfig.companies.path}/${row.rosterCandidate?.roster?.company?.id}`}
                >
                    {row.rosterCandidate?.roster?.company?.value}
                </MuiLink>
            ),
        },
        {
            field: 'added',
            headerName: 'Added',
            width: 110,
            sortIndexField: SortIndexField.RosterCreatedAt,
            valueGetter: (value, row) => row.rosterCandidate?.roster?.createdAt,
            renderCell: ({ row }) =>
                row.rosterCandidate?.roster?.createdAt
                    ? formatDate(
                          row.rosterCandidate.roster.createdAt,
                          'MM/dd/yyyy'
                      )
                    : '—',
        },
        {
            field: 'clientFavorites',
            headerName: 'Favorited',
            flex: 0.25,
            sortable: false,
            renderCell: ({ row }) =>
                row?.rosterCandidate?.clientFavorites &&
                row?.rosterCandidate?.clientFavorites?.length > 0 && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                    >
                        <Favorite fontSize="large" color="error" />
                        <Typography
                            variant="caption"
                            position="absolute"
                            fontWeight="bold"
                            color={theme.palette.common.white}
                        >
                            {row?.rosterCandidate?.clientFavorites?.length}
                        </Typography>
                    </Box>
                ),
        },
        {
            field: 'internalNote',
            headerName: 'Internal Note',
            flex: 1.5,
            sortIndexField: SortIndexField.RosterInternalNote,
            valueGetter: (value, row) =>
                row.rosterCandidate?.internalRecruiterNote?.note,
            renderCell: ({ row }) => {
                const note = row.rosterCandidate?.internalRecruiterNote?.note
                if (!note) return '—'
                return (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        overflow="hidden"
                    >
                        <Typography
                            variant="body2"
                            onClick={(e) => {
                                e.stopPropagation()
                                gridApi.current.toggleDetailPanel(
                                    row.rosterCandidate?.id
                                )
                            }}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: 'inherit',
                                flex: 1,
                                minWidth: 0,
                                cursor: 'pointer',
                            }}
                        >
                            {note}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation()
                                setNoteDrawerRosterCandidateId(
                                    row.rosterCandidate?.id ?? null
                                )
                            }}
                        >
                            <NoteAlt fontSize="small" />
                        </IconButton>
                    </Box>
                )
            },
        },
        {
            field: 'processStatus',
            headerName: 'Process Status',
            flex: 1,
            sortIndexField: SortIndexField.ProcessStatus,
            editable: true,
            valueGetter: (value, row) =>
                row.rosterCandidate?.candidateProcessStatusHistory?.find(
                    (h) => h.isActive
                )?.processStatus?.statusLabel,
            renderEditCell: ({ api, row }) => {
                return (
                    <ProcessStatusEditCell
                        rosterId={row.rosterCandidate.roster?.id ?? ''}
                        canEdit={row.permissions.canEdit}
                        refetchQueries={[GetFilteredRostersWithCandidate]}
                        currentProcessStatus={
                            row.rosterCandidate?.candidateProcessStatusHistory?.find(
                                (h) => h.isActive
                            )?.processStatus
                        }
                        rosterCandidateId={row.rosterCandidate?.id}
                        rowId={row.rosterCandidate?.id}
                        api={api}
                    />
                )
            },
            renderCell: ({ row }) => (
                <ProcessStatusPill
                    processStatusId={
                        row.rosterCandidate?.candidateProcessStatusHistory?.find(
                            (h) => h.isActive
                        )?.processStatus?.id
                    }
                />
            ),
        },
        {
            field: 'processStatusUpdated',
            headerName: 'Process Status Updated At',
            width: 110,
            sortIndexField: SortIndexField.ProcessStatusUpdatedAt,
            valueGetter: (value, row) =>
                row.rosterCandidate?.candidateProcessStatusHistory?.find(
                    (h) => h.isActive
                )?.updatedAt,
            renderCell: ({ row }) => {
                const activeHistory =
                    row.rosterCandidate?.candidateProcessStatusHistory?.find(
                        (h) => h.isActive
                    )
                return activeHistory?.updatedAt
                    ? formatDate(activeHistory.updatedAt, 'MM/dd/yyyy')
                    : '—'
            },
        },
        {
            field: 'sourceStatus',
            headerName: 'Source Status',
            flex: 1,
            sortable: true,
            sortIndexField: SortIndexField.SourceStatus,
            editable: true,
            renderEditCell: ({ row, api }) => (
                <SourceStatusEditCell
                    disabled={!(row.permissions?.canEdit ?? false)}
                    refetchQueries={[GetFilteredRostersWithCandidate]}
                    currentSourceStatus={row.rosterCandidate?.sourceStatus}
                    rosterCandidateId={row.rosterCandidate?.id}
                    rowId={row.rosterCandidate?.id}
                    api={api}
                />
            ),
            renderCell: ({ row }) => (
                <SourceStatusPill status={row.rosterCandidate?.sourceStatus} />
            ),
        },
        {
            field: 'rosterStatus',
            headerName: 'Search Status',
            flex: 1,
            sortable: true,
            sortIndexField: SortIndexField.RosterStatus,
            renderCell: ({ row }) => (
                <RosterStatusPill
                    status={
                        row.rosterCandidate?.roster?.status ??
                        RosterStatus.Draft
                    }
                    sx={undefined}
                />
            ),
        },
    ]

    const rows = (data ?? []) as CandidateRostersTableRowModel[]
    const sortModel = makeSortModel(tableState.sort, columns)

    const handleRowSelectionModelChange = (
        newSelectionModel: GridRowSelectionModel
    ) => {
        const newIds = newSelectionModel
            .map((id) => {
                const row = rows.find((r) => r.rosterCandidate?.id === id)
                return row?.permissions?.canEdit ? (id as string) : null
            })
            .filter((id): id is string => id !== null)
        setSelectedRosterIds(newIds)
    }

    return (
        <Stack sx={{ height: '100%', overflow: 'hidden' }}>
            <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ px: 2, py: 1 }}
            >
                <Typography noWrap fontSize="0.8125rem" color="text.secondary">
                    {isLoading
                        ? 'Fetching results...'
                        : `${totalItems} results`}
                </Typography>

                {selectedRosterIds.length > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            pl: 1.5,
                            ml: 0.5,
                        }}
                    >
                        {(editableTotalCount === null ||
                            selectedRosterIds.length < editableTotalCount) && (
                            <Button
                                variant="text"
                                size="small"
                                onClick={selectAllEditableRosters}
                                disabled={isSelectingAll}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                }}
                            >
                                {isSelectingAll ? (
                                    <>
                                        <CircularProgress
                                            size={12}
                                            sx={{ mr: 0.5 }}
                                        />
                                        Selecting...
                                    </>
                                ) : (
                                    `Select all`
                                )}
                            </Button>
                        )}
                        {editableTotalCount !== null &&
                            selectedRosterIds.length >= editableTotalCount && (
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                    }}
                                >
                                    All selected
                                </Typography>
                            )}
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setSelectedRosterIds([])}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                                color: 'text.secondary',
                            }}
                        >
                            Clear ({selectedRosterIds.length})
                        </Button>
                    </Stack>
                )}

                {selectedRosterIds.length > 0 && (
                    <Box marginLeft="auto">
                        <Button
                            ref={actionsButtonRef}
                            variant="outlined"
                            onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                        >
                            <Stack direction="row" spacing={1}>
                                <Typography variant="button">
                                    Actions
                                </Typography>
                                {actionsAnchorEl ? (
                                    <ArrowDropUp />
                                ) : (
                                    <ArrowDropDown />
                                )}
                            </Stack>
                        </Button>
                        <Menu
                            anchorEl={actionsAnchorEl}
                            open={Boolean(actionsAnchorEl)}
                            onClose={() => setActionsAnchorEl(null)}
                        >
                            <MenuItem
                                onClick={() => {
                                    setProcessStatusOpen(true)
                                    setActionsAnchorEl(null)
                                }}
                            >
                                <ListItemIcon>
                                    <Edit fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Process Status</ListItemText>
                            </MenuItem>
                        </Menu>
                        <Popover
                            open={processStatusOpen}
                            anchorEl={actionsButtonRef.current}
                            onClose={() => setProcessStatusOpen(false)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            slotProps={{
                                paper: {
                                    sx: { marginTop: getPxAsRem(8) },
                                },
                            }}
                        >
                            <Autocomplete
                                onChange={async (_event, value) => {
                                    if (!value) return
                                    const { data } = await updateProcessStatus({
                                        variables: {
                                            rosterCandidateIds:
                                                selectedRosterIds,
                                            processStatus: value.status,
                                        },
                                    })
                                    if (
                                        data
                                            ?.updateCandidateProcessStatusAcrossRosters
                                            .success
                                    ) {
                                        toast({
                                            title: 'Success',
                                            message:
                                                data
                                                    .updateCandidateProcessStatusAcrossRosters
                                                    .message ||
                                                'Process status updated',
                                            severity: 'success',
                                        })
                                    } else {
                                        toast({
                                            title: 'Error',
                                            message:
                                                data
                                                    ?.updateCandidateProcessStatusAcrossRosters
                                                    .message ||
                                                'Error updating process status',
                                            severity: 'error',
                                        })
                                    }
                                    setProcessStatusOpen(false)
                                }}
                                sx={{ width: getPxAsRem(224) }}
                                options={sortProcessStatusesBySummaryGroup(
                                    processStatusOptions?.processStatuses ?? []
                                )}
                                getOptionLabel={(option) => option.statusLabel}
                                isOptionEqualToValue={(option, value) =>
                                    option?.id === value?.id
                                }
                                groupBy={(option) =>
                                    option.processStatusSummaryGroup?.label ||
                                    ''
                                }
                                disableClearable
                                fullWidth
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        placeholder="Select Process Status"
                                    />
                                )}
                            />
                        </Popover>
                    </Box>
                )}
            </Stack>

            <DataGridPro
                apiRef={gridApi}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                checkboxSelection
                disableColumnFilter
                disableColumnMenu
                disableMultipleColumnsSorting
                disableRowSelectionOnClick
                rowSelectionModel={selectedRosterIds}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                isRowSelectable={(params) =>
                    params.row.permissions?.canEdit ?? false
                }
                keepNonExistentRowsSelected
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                loading={isLoading}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={totalItems}
                paginationModel={{
                    page: tableState.page - 1,
                    pageSize: tableState.limit,
                }}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[25, 50, 100]}
                getRowId={(row) =>
                    row.rosterCandidate?.id ??
                    makeUniqueId('onTheseRostersNoRCId')
                }
                rows={rows}
                slots={{
                    noRowsOverlay: getNoRowsOverlay,
                    detailPanelExpandIcon: ChevronRightIcon,
                    detailPanelCollapseIcon: ExpandMoreIcon,
                    footer: TableFooter,
                }}
            />
            <CandidateRosterNoteDrawer
                isOpen={!!noteDrawerRosterCandidateId}
                onClose={() => setNoteDrawerRosterCandidateId(null)}
                onSave={handleNoteSaved}
                rosterCandidateId={noteDrawerRosterCandidateId ?? ''}
                canEdit={
                    rows.find(
                        (r) =>
                            r.rosterCandidate?.id ===
                            noteDrawerRosterCandidateId
                    )?.permissions?.canEdit ?? false
                }
            />
        </Stack>
    )
}

const getNoRowsOverlay = () => (
    <CenteredBox>
        <Typography>No rosters found</Typography>
    </CenteredBox>
)
